import * as React        from "react";
import {cx}              from "emotion";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFileArchive}   from "@fortawesome/free-regular-svg-icons";
import {faFilePdf}       from '@fortawesome/free-solid-svg-icons';

import "./card.scss";

import {ComponentLink, makeChain}   from "@Common/react/cst2";
import {Load2}                      from "@Common/react/load2";
import {UseState}                   from "@Common/react/use-state";
import {downloadProofs}             from "@Common/tools/cc-integration/common/download-proofs";
import {Types}                      from "@Common/types";
import {formatMoney1}               from "@Common/ui-components/language/format-money";

import {formatServiceLevelName} from "@Xpresso/routes/customize/steps/checkout/shipping-section/common/service-level/format-service-level-name";
import {KitCard}                from "@Xpresso/routes/account/orders/common/kit-card/kit-card";

import {
    ApprovalRequestOrderAttribute,
    ApprovalRequestOrderAttributesDict,
    ORDER_ATTRIBUTE_KEY_LABELS
} from "../../../approval-request-attributes";
import {ApprovalRequest} from "../../../approval-requests";

type DownloadPDFAttribute   = Extract<ApprovalRequestOrderAttribute, {key: "downloadPDF"}>;
type DownloadProofAttribute = Extract<ApprovalRequestOrderAttribute, {key: "downloadProof"}>;
type ApprovalRequestOrder   = Types.Utils.ArrayItem<ApprovalRequest['orders']>;

export const Card = ({attributes, order}:{
    attributes  : ApprovalRequestOrderAttributesDict
    order       : Types.Utils.ArrayItem<ApprovalRequest['orders']>,
}) => (
    makeChain<JSX.Element>()
    .then("proofUrlsLoad", ({}, next:ComponentLink<typeof Load2<{
        downloadPDF     : Awaited<ReturnType<DownloadPDFAttribute['loadUrl']>>,
        downloadProof   : Awaited<ReturnType<DownloadProofAttribute['loadUrls']>>,
    }>>) => (
        Load2({next, fetch: async () => ({
            downloadPDF     : await attributes.downloadPDF?.loadUrl(),
            downloadProof   : await attributes.downloadProof?.loadUrls(),
        })})
    ))
    .then("expandedState", ({}, next:ComponentLink<typeof UseState<boolean>>) => UseState({next, initValue: false}))
    .then(({
        expandedState: {onChange: setExpanded, value: expanded},
        proofUrlsLoad: {loading: loadingProofUrls, value: proofUrls},
    }) => (
        <div xd-qa-type="card" xd-qa-name={order.idOrder} className="card-27y card">
            <div className="thumbnail">
                <img src={order.itemThumbnails?.[0]} alt={order?.orderName}/>

                <div className="actions">
                    {attributes.previewProof && (
                        <div className="action" onClick={attributes.previewProof.run}>
                            <i className="fa fa-search" xd-qa-type="icon" xd-qa-name="Search"/>
                            <div className="text" xd-qa-type="label" xd-qa-name="Preview">
                                {ORDER_ATTRIBUTE_KEY_LABELS['previewProof']}
                            </div>
                        </div>
                    )}
                    {attributes.downloadPDF && !loadingProofUrls ? (
                        <a
                            className={cx("action", {"has-error": !proofUrls.downloadPDF.available})}
                            href={proofUrls.downloadPDF.url}
                            target="_blank"
                            onClick={(e) => {
                                if (proofUrls.downloadPDF.message) {
                                    alert(proofUrls.downloadPDF.message);
                                }
                                if (!proofUrls.downloadPDF.available) {
                                    e.preventDefault();
                                }
                            }}
                        >
                            <FontAwesomeIcon className="fa fa-search" icon={faFilePdf} xd-qa-type="icon" xd-qa-name="PDF"/>
                            <div className="text" xd-qa-type="label" xd-qa-name="PDF">
                                {ORDER_ATTRIBUTE_KEY_LABELS['downloadPDF']}
                            </div>
                        </a>
                    ) : !!loadingProofUrls && (
                            <div>
                                <i className="fa fa-spinner fa-pulse"/>
                            </div>
                    )}

                    {attributes.downloadProof && !loadingProofUrls && proofUrls.downloadProof?.available && (
                        <div
                            className="action"
                            onClick={() => {
                                downloadProofs(proofUrls.downloadProof.urls, order.idOrder, Object.keys(proofUrls.downloadProof.urls));
                            }}
                        >
                            <FontAwesomeIcon
                                className="fa fa-search"
                                icon={faFileArchive}
                                xd-qa-type="icon"
                                xd-qa-name={ORDER_ATTRIBUTE_KEY_LABELS['downloadProof']}
                            />
                            <div
                                className="text"
                                xd-qa-type="label"
                                xd-qa-name={ORDER_ATTRIBUTE_KEY_LABELS['downloadProof']}
                            >
                                {ORDER_ATTRIBUTE_KEY_LABELS['downloadProof']}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="main">
                <div className="line1">
                    <div className="id" xd-qa-type="label" xd-qa-name="Order ID">
                        {order.idOrder}
                    </div>

                    <div className="extra">
                        <div className="items">

                            <div className="item">
                                <div className="label" xd-qa-type="label" xd-qa-name="Service Level">
                                    Service Level:
                                </div>
                                <div className="value" xd-qa-type="text" xd-qa-name="Service Level">
                                    {formatServiceLevelName(order.deliveryService)}
                                </div>
                            </div>

                            {"quantity" in order && (
                                <>
                                    <div className="separator">
                                        &middot;
                                    </div>

                                    <div className="item">
                                        <div className="label" xd-qa-type="label" xd-qa-name="Quantity">
                                            Quantity:
                                        </div>
                                        <div className="value" xd-qa-type="text" xd-qa-name="Quantity">
                                            {order.quantity}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {attributes.price && (
                            <div className="summary" xd-qa-type="text" xd-qa-name="Subtotal">
                                {formatMoney1(attributes.price.value)}
                            </div>
                        )}
                    </div>
                </div>

                <div className="title" xd-qa-type="text" xd-qa-name="Name">
                    { !order?.orderName || order?.orderName === " " ? (
                        order?.templateName ?? order?.orderType ?? "Brochure"
                    ) : (
                        order?.orderName
                    )}
                </div>

                <div className="actions">
                    {attributes.edit && (
                        <button
                            onClick={attributes.edit.run}
                            xd-qa-type="link"
                            xd-qa-name="Edit Order"
                        >
                            {attributes.edit.callToAction || ORDER_ATTRIBUTE_KEY_LABELS['edit']}
                        </button>
                    )}
                    {attributes.deny && (
                        <button
                            onClick={attributes.deny.run}
                            className="danger"
                            xd-qa-type="link"
                            xd-qa-name="Deny"
                        >
                            {attributes.deny.callToAction || ORDER_ATTRIBUTE_KEY_LABELS['deny']}
                        </button>
                    )}
                    {attributes.showDetails && (
                        <button
                            onClick={attributes.showDetails.run}
                            xd-qa-type="link"
                            xd-qa-name="Order Details"
                        >{ORDER_ATTRIBUTE_KEY_LABELS['showDetails']}</button>
                    )}
                </div>

                {!attributes.corrupted && <div className="items">
                    {ORDER_INFOS.map(({label, mapValue}) => (
                        <div className="item" key={label}>
                            <div className="label" xd-qa-type="label" xd-qa-name={label}>
                                {label}
                            </div>
                            <div className="value" xd-qa-type="text" xd-qa-name={label}>
                                {mapValue(order)}
                            </div>
                        </div>
                    ))}
                    {attributes.kit?.isKit && (
                        <div className="item" key="Kit Components">
                            <div className="label" xd-qa-type="label" xd-qa-name="Kit Components">Kit Components ({attributes.kit.components.length})</div>
                            <div className="value">
                                <button onClick={() => setExpanded(!expanded)} xd-qa-type="link" xd-qa-name={expanded ? "Hide" : "Show"}>{expanded ? "Hide" : "Show"}</button>
                            </div>
                        </div>
                    )}
                </div>}
            </div>

            {expanded && attributes.kit?.isKit && (
                <div className="kit-components">
                    {attributes.kit.components.map((component) => (

                        <KitCard key={component.idTemplate} component={component}
                                itemId={order.idOrder} isPendingTransaction="true"
                                isApproval="true" />
                    ))}
                </div>
            )}
        </div>
    ))
    .run()
);

const ORDER_INFOS:Array<{
    label       : string,
    mapValue    : (order:ApprovalRequestOrder) => React.ReactNode,
}> = [
    {
        label       : "Type",
        mapValue    : (order) => order.orderType,
    },
    {
        label       : "Category",
        mapValue    : (order) => order.category?.name,
    },
    {
        label       : "Subcategory",
        mapValue    : (order) => order.subCategory?.name,
    },
    {
        label       : "Style",
        mapValue    : (order) => order.style?.name,
    },
    {
        label       : "Template",
        mapValue    : (order) => order.templateName,
    },
];

